import * as React from 'react'
import { ActivityIndicator, View } from 'react-native'
import { useLayoutEffect } from 'react'
import { useNavigation, useRoute } from '@react-navigation/native'
import { useAuthContext } from '../contexts/auth-context'
import type { AppNavigation } from './types'
import { getReturnPath } from './build-return-path'

/**
 * Wraps screens that require a logged-in user. Anonymous users are sent to
 * `LoginMobileNumber` with `returnPath` so `goAfterLogin` can restore the route.
 */
export function withRequiredAuth<P extends object>(
  Wrapped: React.ComponentType<P>
): React.FC<P> {
  function RequireAuthGate(props: P) {
    const { isAnonymous, loading } = useAuthContext()
    const navigation = useNavigation<AppNavigation>()
    const route = useRoute()

    useLayoutEffect(() => {
      if (loading) {
        return
      }
      if (!isAnonymous) {
        return
      }
      const returnPath = getReturnPath(navigation) ?? 'ListNodes'
      navigation.replace('LoginMobileNumber', { returnPath })
    }, [loading, isAnonymous, navigation, route.name, route.params])

    if (loading) {
      return (
        <View
          style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}
        >
          <ActivityIndicator />
        </View>
      )
    }
    if (isAnonymous) {
      return null
    }
    return <Wrapped {...props} />
  }

  RequireAuthGate.displayName = `RequireAuth(${
    Wrapped.displayName || Wrapped.name || 'Screen'
  })`
  return RequireAuthGate
}
